import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, InputNumber, Space, Typography, Alert, message } from 'antd';
import {
  getMqttMappingByMeter,
  mqttPublishByMeter,
  getMqttLatestMessage,
  uploadDeviceReading,
  updateDevice,
} from '../api';
import {
  getAdminMeterPublishKey,
  remotePayloadUseBleDirect,
  buildFinalPayload,
  buildRemoteSetPowerHex,
  adminRemoteRead51,
  yjRemoteMqttRxWaitMsByMappingTier,
} from '../utils/yjMeterRemote';

const { Text } = Typography;

/** 补量弹窗已占用一层 Modal（默认 zIndex≈1000），静态 Modal.confirm/error 需更高，否则压在下面像「没反应」 */
const NESTED_DIALOG_Z_INDEX = 2100;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** 抄表后同步列表剩余/累计：小程序抄表接口按 user 校验，管理员常报「用户不存在或已禁用」，改走管理端更新设备 */
async function syncReadingViaAdminUpdate(dev, remain, totalPower) {
  const payload = {
    factory_meter_no: dev.factory_meter_no ?? '',
    current_meter_no: dev.current_meter_no ?? '',
    device_status: Number(dev.device_status) === 0 ? 0 : 1,
    electric_balance: Number(dev.electric_balance) || 0,
    electric_quantity: remain,
    min_recharge: Number(dev.min_recharge) || 0,
    management_fee: Number(dev.management_fee) || 0,
    bind_room_id: Number(dev.bind_room_id) || 0,
    device_mac: dev.device_mac != null ? String(dev.device_mac).trim() : '',
  };
  if (totalPower != null && !Number.isNaN(Number(totalPower))) {
    payload.total_electricity = Number(totalPower);
  }
  if (dev.communication_type !== undefined && dev.communication_type !== null) {
    payload.communication_type = Number(dev.communication_type);
  }
  if (dev.device_detail_type !== undefined && dev.device_detail_type !== null) {
    payload.device_detail_type = Number(dev.device_detail_type);
  }
  return updateDevice(dev.id, payload);
}

/**
 * 管理员远程抄表 + 补电量/补水量（与小程序 test1 远程 51/45 协议一致）
 * @param {'electric'|'water'} variant
 */
const MeterSupplementModal = ({
  open,
  device,
  variant,
  onClose,
  onRefresh,
  supplementBlockedUntilRead = false,
  onSupplementIssued,
  onMeterReadSuccess,
}) => {
  const [readLoading, setReadLoading] = useState(false);
  const [readRemain, setReadRemain] = useState(null);
  const [readTotal, setReadTotal] = useState(null);
  const [readHint, setReadHint] = useState('');
  const [readError, setReadError] = useState('');

  const [setRemainVal, setSetRemainVal] = useState(null);
  const [supplementVal, setSupplementVal] = useState(null);
  const [reduceVal, setReduceVal] = useState(null);
  const [supplementLoading, setSupplementLoading] = useState(false);
  const isWater = variant === 'water';
  const unitLabel = isWater ? '吨' : '度';
  const listRemain = Number(device?.electric_quantity ?? 0);
  const listTotal = Number(device?.total_electricity ?? 0);
  const actionLabel = isWater ? '补水量' : '补电量';
  const reduceActionLabel = isWater ? '减少水量' : '减少电量';
  const setRemainActionLabel = isWater ? '更改剩余水量' : '更改剩余电量';

  const resetState = useCallback(() => {
    setReadLoading(false);
    setReadRemain(null);
    setReadTotal(null);
    setReadHint('');
    setReadError('');
    setSetRemainVal(null);
    setSupplementVal(null);
    setReduceVal(null);
    setSupplementLoading(false);
  }, []);

  useEffect(() => {
    if (open) resetState();
  }, [open, device?.id, resetState]);

  const handleReadMeter = async () => {
    if (!device) return;
    setReadLoading(true);
    setReadError('');
    setReadHint('正在下发抄表指令…');
    setReadRemain(null);
    setReadTotal(null);

    const publishKey = getAdminMeterPublishKey(device);
    if (!publishKey) {
      setReadLoading(false);
      setReadError('无法确定表号或 MAC，请检查设备档案');
      return;
    }

    const pollReadOnce = async () => {
      const mapRes = await getMqttMappingByMeter(publishKey);
      const tier = Number(mapRes?.data?.mapping_fanout_tier) === 2 ? 2 : 1;
      const waitSec = Math.round(yjRemoteMqttRxWaitMsByMappingTier(tier) / 1000);
      setReadHint(`已下发，等待设备回调（最多约 ${waitSec} 秒，与小程序远程一致）…`);
      return adminRemoteRead51({
        device,
        getMqttMappingByMeter,
        mqttPublishByMeter,
        getMqttLatestMessage,
        useWebSocket: true,
      });
    };

    const applyReadSuccess = async (parsed) => {
      setReadRemain(parsed.remain);
      setReadTotal(parsed.total);
      setReadHint('抄表成功');

      let uploadErr = '';
      let synced = false;
      try {
        const uploadRes = await uploadDeviceReading({
          device_id: String(device.id),
          remaining_power: parsed.remain,
          total_electric_power: parsed.total,
          is_manual: false,
          source_page: 'admin-pc',
        });
        if (uploadRes?.success) synced = true;
        else uploadErr = (uploadRes?.error || uploadRes?.msg || '').toString();
      } catch (ex) {
        uploadErr = (ex?.response?.data?.error || ex?.response?.data?.msg || ex?.message || '').toString();
      }

      if (!synced) {
        const adminRes = await syncReadingViaAdminUpdate(device, parsed.remain, parsed.total);
        if (adminRes?.success) {
          synced = true;
          if (/用户不存在|已禁用/.test(uploadErr)) {
            message.success(
              `抄表成功，列表中的剩余${isWater ? '水量' : '电量'}已更新（抄表上传接口不识别管理员账号，已改用管理端保存）`
            );
          } else if (uploadErr) {
            message.success(`抄表成功，列表已更新（抄表上传：${uploadErr}）`);
          } else {
            message.success(`抄表成功，列表已更新剩余${isWater ? '水量' : '电量'}`);
          }
        } else {
          const adminErr = (adminRes?.error || '').toString();
          if (uploadErr || adminErr) {
            message.warning(
              [uploadErr && `抄表同步：${uploadErr}`, adminErr && `管理端更新：${adminErr}`].filter(Boolean).join('；') ||
                '同步失败'
            );
          }
        }
      }

      if (synced) {
        onRefresh?.();
      }

      if (device?.id != null) onMeterReadSuccess?.(device.id);
    };

    try {
      const parsed = await pollReadOnce();
      if (!parsed) {
        setReadError('');
        setReadHint('抄表超时：未在约定时间内收到有效 51 回包，可稍后重试（主表约 8 秒、副表约 16 秒，与小程序一致）。');
        return;
      }
      await applyReadSuccess(parsed);
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '抄表失败';
      setReadHint('');
      setReadError(errMsg);
    } finally {
      setReadLoading(false);
    }
  };

  /** 下发 45 写绝对剩余（度/吨） */
  const publishRemainTarget = async (targetRemain, { dialogTitle, onSuccessClear }) => {
    if (!device || supplementBlockedUntilRead) return;

    setSupplementLoading(true);
    try {
      const publishKey = getAdminMeterPublishKey(device);
      if (!publishKey) {
        throw new Error('无法确定表号或 MAC，请检查设备档案');
      }

      const target = Math.max(0, Number(targetRemain) || 0);

      const isBleDirect = remotePayloadUseBleDirect(publishKey);
      const rawNoCrc = buildRemoteSetPowerHex(publishKey, target, isBleDirect);
      const payload = buildFinalPayload(rawNoCrc, true);

      const tryPublish = () =>
        mqttPublishByMeter({
          meter_no: publishKey,
          payload,
          qos: 0,
        });

      let pubRes = await tryPublish();
      if (!pubRes?.success) {
        setSupplementLoading(false);
        Modal.confirm({
          zIndex: NESTED_DIALOG_Z_INDEX,
          title: `${dialogTitle}发送失败`,
          content: `${pubRes?.msg || pubRes?.error || '指令发送失败'}\n是否再试一次？`,
          okText: '重试',
          cancelText: '取消',
          onOk: async () => {
            setSupplementLoading(true);
            try {
              pubRes = await tryPublish();
              if (!pubRes?.success) {
                Modal.error({
                  zIndex: NESTED_DIALOG_Z_INDEX,
                  title: `${dialogTitle}失败`,
                  content: pubRes?.msg || pubRes?.error || '指令发送失败',
                });
                return;
              }
              message.success(`${dialogTitle}指令已下发，请先点「抄表」核对`);
              if (device?.id != null) onSupplementIssued?.(device.id);
              onSuccessClear?.();
            } finally {
              setSupplementLoading(false);
            }
          },
        });
        return;
      }
      message.success(`${dialogTitle}指令已下发，请先点「抄表」核对`);
      if (device?.id != null) onSupplementIssued?.(device.id);
      onSuccessClear?.();
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '发送失败';
      Modal.error({
        zIndex: NESTED_DIALOG_Z_INDEX,
        title: `${dialogTitle}失败`,
        content: errMsg,
      });
    } finally {
      setSupplementLoading(false);
    }
  };

  /** 以抄表/列表剩余为基准增减 */
  const publishRemainDelta = async (delta, opts) => {
    const baseRemain =
      readRemain != null && !Number.isNaN(readRemain) ? readRemain : listRemain;
    return publishRemainTarget(Math.max(0, baseRemain + delta), opts);
  };

  const handleSetRemain = () => {
    const target = Number(setRemainVal);
    if (setRemainVal == null || Number.isNaN(target) || target < 0) {
      message.warning(`请输入有效的目标剩余（${unitLabel}，≥0，最多两位小数）`);
      return;
    }
    publishRemainTarget(target, {
      dialogTitle: setRemainActionLabel,
      onSuccessClear: () => setSetRemainVal(null),
    });
  };

  const handleSupplement = () => {
    const delta = Number(supplementVal);
    if (supplementVal == null || Number.isNaN(delta) || delta <= 0) {
      message.warning(`请输入大于 0 的数值（${unitLabel}，最多两位小数）`);
      return;
    }
    publishRemainDelta(delta, {
      dialogTitle: actionLabel,
      onSuccessClear: () => setSupplementVal(null),
    });
  };

  const handleReduce = () => {
    const delta = Number(reduceVal);
    if (reduceVal == null || Number.isNaN(delta) || delta >= 0) {
      message.warning(`请输入小于 0 的数值（${unitLabel}，最多两位小数，如 -1.25）`);
      return;
    }
    publishRemainDelta(delta, {
      dialogTitle: reduceActionLabel,
      onSuccessClear: () => setReduceVal(null),
    });
  };

  return (
    <Modal
      title={`${actionLabel} — ${device?.current_meter_no || device?.id || ''}`}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          关闭
        </Button>,
      ]}
      width={520}
      zIndex={1000}
      destroyOnClose
      maskClosable={false}
    >
      {device && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">列表同步 · 当前剩余{isWater ? '水量' : '电量'}（{unitLabel}）</Text>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{listRemain.toFixed(2)}</div>
            <Text type="secondary" style={{ marginTop: 4, display: 'block' }}>
              列表同步 · 累计{isWater ? '用水' : '用电'}（{unitLabel}）
            </Text>
            <div style={{ fontSize: 15 }}>{listTotal.toFixed(2)}</div>
          </div>

          {readError && <Alert type="error" message={readError} showIcon />}
          {readHint && !readError && (
            <Alert
              type={
                readHint === '抄表成功'
                  ? 'success'
                  : readHint.includes('抄表超时')
                    ? 'warning'
                    : 'info'
              }
              message={readHint}
              showIcon
            />
          )}

          {(readRemain != null || readTotal != null) && (
            <div>
              <Text type="secondary">抄表结果 · 剩余{isWater ? '水量' : '电量'}（{unitLabel}）</Text>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{(readRemain ?? 0).toFixed(3)}</div>
              <Text type="secondary" style={{ marginTop: 4, display: 'block' }}>
                抄表结果 · 累计{isWater ? '用水' : '用电'}（{unitLabel}）
              </Text>
              <div style={{ fontSize: 15 }}>{(readTotal ?? 0).toFixed(3)}</div>
            </div>
          )}

          <Space wrap>
            <Button type="primary" loading={readLoading} onClick={handleReadMeter}>
              抄表
            </Button>
          </Space>

          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Text strong>{setRemainActionLabel}</Text>
            <div style={{ marginTop: 8 }}>
              <Space wrap align="center">
                <span>目标剩余（{unitLabel}，最多两位小数）</span>
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  value={setRemainVal}
                  onChange={setSetRemainVal}
                  placeholder="如 10 补到 30 填 30"
                  style={{ width: 180 }}
                  disabled={supplementBlockedUntilRead}
                />
                <Button
                  type="primary"
                  loading={supplementLoading}
                  onClick={handleSetRemain}
                  disabled={
                    supplementBlockedUntilRead || setRemainVal == null || Number(setRemainVal) < 0
                  }
                >
                  确认更改
                </Button>
              </Space>
            </div>
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              {supplementBlockedUntilRead
                ? `已下发过补量，请先点击「抄表」后再${setRemainActionLabel}。`
                : `直接填写写表后的目标剩余（如当前 10${unitLabel}要补到 30${unitLabel}填 30，要减到 5${unitLabel}填 5）。下发后请点「抄表」核对。`}
            </Text>
          </div>

          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Text strong>{actionLabel}</Text>
            <div style={{ marginTop: 8 }}>
              <Space wrap align="center">
                <span>增加（{unitLabel}，最多两位小数）</span>
                <InputNumber
                  min={0.01}
                  step={0.01}
                  precision={2}
                  value={supplementVal}
                  onChange={setSupplementVal}
                  placeholder="如 1.25"
                  style={{ width: 140 }}
                  disabled={supplementBlockedUntilRead}
                />
                <Button
                  type="primary"
                  loading={supplementLoading}
                  onClick={handleSupplement}
                  disabled={
                    supplementBlockedUntilRead || supplementVal == null || Number(supplementVal) <= 0
                  }
                >
                  {actionLabel}
                </Button>
              </Space>
            </div>
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              {supplementBlockedUntilRead
                ? `已下发过补量，请先点击「抄表」后再${actionLabel}。`
                : `以「抄表剩余」为基准增加；若尚未抄表成功则使用列表剩余。指令下发后不等待设备回包，请稍后点「抄表」核对。`}
            </Text>
          </div>

          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Text strong>减少</Text>
            <div style={{ marginTop: 8 }}>
              <Space wrap align="center">
                <span>减少（{unitLabel}，最多两位小数，请输入负数）</span>
                <InputNumber
                  max={-0.01}
                  step={0.01}
                  precision={2}
                  value={reduceVal}
                  onChange={setReduceVal}
                  placeholder="如 -1.25"
                  style={{ width: 140 }}
                  disabled={supplementBlockedUntilRead}
                />
                <Button
                  danger
                  loading={supplementLoading}
                  onClick={handleReduce}
                  disabled={
                    supplementBlockedUntilRead || reduceVal == null || Number(reduceVal) >= 0
                  }
                >
                  减少
                </Button>
              </Space>
            </div>
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              {supplementBlockedUntilRead
                ? '已下发过补量，请先点击「抄表」后再减少。'
                : `以「抄表剩余」为基准扣减（输入负数，如 -2 表示减 2${unitLabel}）；目标剩余不会低于 0。下发后请点「抄表」核对。`}
            </Text>
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default MeterSupplementModal;
